<template>
  <div @mousedown.stop @click.stop>
    <div v-if="readonly">
      <input class="readonly-one" :readonly="true" :value="readonlyDisplayValue" />
    </div>
    <div v-else class="flex gap-4">
      <div v-for="(_set, _set_i) in setting" :key="_set_i" class="relative flex-1" :class="[_set_i < setting.length - 1 && 'after-hyphen']" :style="_set.style">
        <input ref="hyphenInput" :type="_set.type ? _set.type : 'text'" :maxlength="_set.max" :value="displayValue(_set_i)" @keydown="onKeydown($event, _set_i, _set)" @input="onInput($event.target.value, _set_i, _set)" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HyphenInput",
  props: {
    value: [String, Number],
    readonly: Boolean,
    autocomplete: { type: Boolean, default: true },

    // true면 emit 데이터는 하이픈 없이 보냄 (표시는 하이픈 유지)
    isNoHyphenData: { type: Boolean, default: false },

    // [{ max: 4, hidden: true }]
    setting: { type: Array, default: () => [{ max: 3 }, { max: 4 }, { max: 4 }] },
  },

  data() {
    return {
      inputValue: this.splitValue(String(this.value || "")),
    };
  },

  computed: {
    currentRawValue() {
      return (this.inputValue || []).join("");
    },
    readonlyDisplayValue() {
      const text = this.setting
        .map((set, i) => {
          const v = this.inputValue[i] || "";
          if (set && set.hidden && v) return "*".repeat(v.length);
          return v;
        })
        .join("-");

      return this.isNoHyphenData ? text.replace(/-/g, "") : text;
    },
  },

  watch: {
    value: {
      handler(newVal) {
        const incoming = String(newVal || "");
        const incomingRaw = this.normalize(incoming);
        if (incomingRaw === this.currentRawValue) return;
        this.inputValue = this.splitValue(incoming);
      },
    },
    inputValue: {
      immediate: true,
      deep: true,
      handler(arr) {
        const joined = (arr || []).join("-");
        const emitted = this.isNoHyphenData ? joined.replace(/-/g, "") : joined;
        this.$emit("input", emitted);
      },
    },
  },

  methods: {
    normalize(str) {
      return (str || "").replace(/-/g, "");
    },

    /**
     * 문자열을 칸별로 자른다
     * - 하이픈이 있으면 split
     * - 없으면 setting.max 기준으로만 "초기" 세팅에 쓰고,
     *   이후엔 value watcher에서 되돌아온 값은 무시
     */
    splitValue(str) {
      const sets = this.setting || [];
      const s = String(str || "");

      if (!sets.length) return [this.normalize(s)];

      // 하이픈이 있으면 그대로 분해
      if (s.indexOf("-") !== -1) {
        const parts = s.split("-");
        while (parts.length < sets.length) parts.push("");
        return parts.slice(0, sets.length);
      }

      // 하이픈이 없으면 max로 잘라 초기 분해
      const raw = this.normalize(s);
      const result = [];
      let startIndex = 0;

      sets.forEach((set) => {
        const max = set && set.max ? set.max : 0;
        result.push(raw.slice(startIndex, startIndex + max));
        startIndex += max;
      });

      while (result.length < sets.length) result.push("");
      return result.slice(0, sets.length);
    },

    displayValue(index) {
      const val = this.inputValue[index] || "";
      const set = this.setting[index] || {};
      if (set.hidden && val) return "*".repeat(val.length);
      return val;
    },

    onInput(typed, index, set) {
      if (set && set.hidden) {
        this.$nextTick(() => {
          const el = this.$refs.hyphenInput && this.$refs.hyphenInput[index];
          if (el) el.value = this.displayValue(index);
        });
        return;
      }

      const max = set && set.max ? set.max : 0;
      this.$set(this.inputValue, index, (typed || "").slice(0, max));
      this.nextFocus(index, (this.inputValue[index] || "").length, max);
    },

    onKeydown(e, index, set) {
      if (!(set && set.hidden)) return;

      const max = set && set.max ? set.max : 0;
      const prev = this.inputValue[index] || "";

      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === "Backspace") {
        e.preventDefault();
        this.$set(this.inputValue, index, prev.slice(0, Math.max(0, prev.length - 1)));
        return;
      }

      if (e.key === "Delete") {
        e.preventDefault();
        this.$set(this.inputValue, index, prev.slice(0, Math.max(0, prev.length - 1)));
        return;
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Tab" || e.key === "Enter" || e.key === "Home" || e.key === "End") {
        return;
      }

      if (e.key && e.key.length === 1) {
        e.preventDefault();
        if (prev.length >= max) return;

        this.$set(this.inputValue, index, (prev + e.key).slice(0, max));
        this.nextFocus(index, (this.inputValue[index] || "").length, max);
        return;
      }

      e.preventDefault();
    },

    nextFocus(index, length, max) {
      if (this.autocomplete && length === max && this.$refs.hyphenInput && this.$refs.hyphenInput[index + 1]) {
        this.$refs.hyphenInput[index + 1].focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
input {
  position: relative;
  flex: 1;
  width: 100%;
  font-size: 1rem !important;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  box-sizing: border-box;
  box-shadow: 0 0 0 0 rgb(0 0 0 / 15%);
  outline: none;
  text-transform: none;
  text-decoration: none;
  padding: 0.7rem !important;
  transition: all 0.3s ease;

  &:focus {
    border: 1px solid rgba(var(--vs-primary), 1) !important;
    box-shadow: 0 3px 10px 0 rgb(0 0 0 / 15%);
  }
}

.readonly-one {
  width: 100%;
}

.after-hyphen {
  display: flex;
  align-items: center;

  &::after {
    content: "-";
    position: absolute;
    right: -9px;
  }
}
</style>
